import { POSTING_SOURCE_IDS, type PostingSourceId } from '@revelio/data-access';
import { countryIdFromNestedGeographyId } from '@revelio/iso-utility';

import {
  type Filter,
  FilterId,
  FilterType,
  type PrimaryFilterId,
} from '../types/filters';

// Global minimum start-date applied to all providers (per 2024-03-08 refresh requirement)
export const GLOBAL_MIN_START_DATE = '2022-03-08';

const WEBSITE_POSTINGS_MIN_START_DATE = '2022-08-15';
const INDEED_MIN_START_DATE = '2023-10-01';

const COUNTRIES_WITH_INDEED_EARLY_GEO = ['1', '4', '39']; // UK, US, HK

const getIndeedMinStartDate = ({ filters = [] }: { filters?: Filter[] }) => {
  const geoFilter = filters.find((filter) => filter.id === FilterId.GEOGRAPHY);
  if (geoFilter?.type !== FilterType.TREE) return INDEED_MIN_START_DATE;

  // Shared with the assistant's page scope and its postings read, which both
  // have to floor an Indeed window exactly where this does.
  const countrySet = new Set(
    (geoFilter?.selections ?? [])
      .map((selection) =>
        countryIdFromNestedGeographyId(selection.id, selection.levelId)
      )
      .filter((country): country is number => country !== null)
      .map(String)
  );
  const hasEarlyStartCountry = COUNTRIES_WITH_INDEED_EARLY_GEO.some((country) =>
    countrySet.has(country)
  );
  return hasEarlyStartCountry ? GLOBAL_MIN_START_DATE : INDEED_MIN_START_DATE;
};

export const getProviderMinStartDate = ({
  providerIds,
  primaryDimension,
  filters,
}: {
  providerIds: PostingSourceId[];
  primaryDimension: PrimaryFilterId;
  filters?: Filter[];
}) => {
  const dates = providerIds.map((providerId) => {
    switch (providerId) {
      case POSTING_SOURCE_IDS.unified:
        return GLOBAL_MIN_START_DATE;
      case POSTING_SOURCE_IDS.linkedIn:
        return GLOBAL_MIN_START_DATE;
      case POSTING_SOURCE_IDS.otherWebsitePostings:
      case POSTING_SOURCE_IDS.revelioWebsitePostings:
        return WEBSITE_POSTINGS_MIN_START_DATE;
      case POSTING_SOURCE_IDS.indeed:
        return primaryDimension === FilterId.GEOGRAPHY ||
          primaryDimension === FilterId.ROLE ||
          primaryDimension === FilterId.CUSTOM_SKILL ||
          primaryDimension === FilterId.ACTIVITY
          ? getIndeedMinStartDate({ filters })
          : INDEED_MIN_START_DATE;
      default:
        return GLOBAL_MIN_START_DATE;
    }
  });
  return dates.sort()[0] ?? GLOBAL_MIN_START_DATE;
};
